import React from 'react';
import PropTypes from 'prop-types';
import { BrandingLogoSizes } from './BrandingLogoSizes';
import { brandingSites } from './brandingSites';
import { resolvePublicUrl } from '../../helpers/resolvePublicUrl';

export const BrandingLogo = ({
    className,
    disableDefaultFallback,
    onLoad,
    returnUrl,
    size,
    style,
    subdomain,
}) => {
    if (!subdomain) subdomain = getCurrentSubdomain(returnUrl);

    const metadata = getBrandingImageMetadata(subdomain, size, disableDefaultFallback);
    if (!metadata) return null;

    const { src, width, height } = metadata;
    return (
        <img
            className={className}
            height={height}
            onLoad={onLoad}
            src={src}
            style={style}
            width={width}
        />
    );
};

BrandingLogo.propTypes = {
    className: PropTypes.string,
    disableDefaultFallback: PropTypes.bool,
    onLoad: PropTypes.func,
    returnUrl: PropTypes.string,
    subdomain: PropTypes.string,
    size: PropTypes.oneOf([
        BrandingLogoSizes.Halftone,
        BrandingLogoSizes.Large,
        BrandingLogoSizes.Medium,
        BrandingLogoSizes.Small,
        BrandingLogoSizes.XSmall,
        BrandingLogoSizes.XXSmall,
    ]).isRequired,
    style: PropTypes.object,
    subdomain: PropTypes.string,
};

const getBrandingImageMetadata = (subdomain, size, disableDefaultFallback) => {
    const brandingPath = `${resolvePublicUrl()}/images/branding/${size}`;
    let brandingSiteCandidate = brandingSites[subdomain] ?? brandingSites[subdomain.toLowerCase()];

    // if the branding site exists but the requested size doesn't, clear our candidate
    if (brandingSiteCandidate && !brandingSiteCandidate.sizes[size]) brandingSiteCandidate = null;

    // if there is no candidate and fallback is disabled, return nothing
    if (!brandingSiteCandidate && disableDefaultFallback) return null;

    const brandingSite = brandingSiteCandidate ?? brandingSites.default;
    const brandingSizes = brandingSite.sizes[size];
    const src = `${brandingPath}/${brandingSite.logo}`;

    return { src, ...brandingSizes };
};

// this copies logic in UriExtensions.GetSubDomain() to mimic MVC functionality
function getCurrentSubdomain(returnUrl) {
    var hostname = window.location.hostname; // returns "subdomain.domain.topleveldomain"

    // If we actually need to use the referrer hostname then do this
    if (returnUrl?.indexOf('http') === 0) {
        var url = new URL(returnUrl);
        hostname = url.hostname;
    }

    const hostnameParts = hostname.split('.'); // ["subdomain","domain","topleveldomain"]

    let subdomain = null;

    if (hostnameParts.length >= 3) {
        if (hostnameParts.length > 3) {
            // if A.B...C.subdomain.domain.topleveldomain, return subdomain
            subdomain = hostnameParts[hostnameParts.length - 3];
        } else {
            subdomain = hostnameParts[0];
        }
    } else if (hostnameParts.length > 0 && hostnameParts[0] === 'localhost') {
        subdomain = 'localdev';
    } else {
        subdomain = 'dev';
    }

    // handle frontdoor-<env> domains. Ex: frontdoor-dev.carecontinuity.com will return dev
    if (subdomain.startsWith('frontdoor-')) subdomain = subdomain.split('-')[1];
    // handle dev and demo environments. Ex: tandigm-dev.carecontinuity.com will return tandigm
    if (subdomain.endsWith('-dev') || subdomain.endsWith('-demo'))
        subdomain = subdomain.split('-')[0];

    return subdomain;
}
